import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { closeDialog, selectCurrentDialog } from "@/store/profileSlice";
import { AlertCircle } from "lucide-react";

interface KycDialogErrorProps {
  error: Error;
  resetErrorBoundary: () => void;
}

export function KycDialogError({
  error,
  resetErrorBoundary,
}: KycDialogErrorProps) {
  const isMobile = useMediaQuery("(max-width:768px)");
  const dialog = useAppSelector(selectCurrentDialog);
  const dispatch = useAppDispatch();

  return (
    <Sheet
      open={dialog === "kycDetails"}
      onOpenChange={(val) => {
        if (!val) {
          dispatch(closeDialog());
        }
      }}
    >
      <SheetContent
        className="md:max-w-[500px] font-outfit px-4 lg:px-6 rounded-t-xl md:rounded-t-none overflow-y-scroll max-h-[70vh] md:max-h-screen"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
        }}
        side={isMobile ? "bottom" : "right"}
      >
        <SheetHeader>
          <SheetTitle className="text-center pb-10">KYC</SheetTitle>
          <SheetDescription className="sr-only">
            Upgrade your KYC
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col items-center justify-center gap-6 py-12">
          <div className="w-16 h-16 rounded-full bg-alert-50 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-alert-500" />
          </div>

          <div className="flex flex-col gap-2 text-center max-w-sm">
            <h3 className="text-lg font-semibold text-primary-900">
              Something went wrong
            </h3>
            <p className="text-sm text-primary-500">
              We encountered an error while loading your KYC information. Please
              try again.
            </p>
            {process.env.NODE_ENV === "development" && (
              <details className="mt-4 text-left">
                <summary className="text-xs text-alert-500 cursor-pointer">
                  Error details
                </summary>
                <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto">
                  {error.message}
                </pre>
              </details>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              onClick={resetErrorBoundary}
              className="px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 active:bg-primary-800 transition-colors font-medium"
            >
              Try again
            </button>
            <button
              onClick={() => dispatch(closeDialog())}
              className="px-6 py-2.5 border border-border-300 text-primary-700 rounded-lg hover:bg-primary-50 active:bg-primary-100 transition-colors font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
